import { IconButton, Stack } from '@mui/material';
import React, { useContext, useState } from 'react';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { CustomCellProps } from 'components/common/grid/';
import IconMi from 'components/common/icon/IconMi';
import { prepareFormComponentValues } from 'components/common/form/layout/control';
import { RawFormComponentType, FormDataAPIType } from 'components/common/form';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import { prepareFormElementProps } from 'components/common/form/formTools';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { FormElementWrapper } from 'components/common/form/element/FormElementWrapper';
import useBundleTranslation from 'i18n';
import useGridDeleteConfirmation from 'components/common/grid/buttons/useGridDeleteConfirmation';
import { IKpiChartOverlay } from '@report-content';
import { KpiChartOverlaysContext } from './KpiChartOverlaysContext';
import { ChartLineTypes } from 'components/chart';

function FormColorPicker({ elementProps }: { elementProps: FormDataAPIType }) {
    const colorPickerProps = prepareFormElementProps({
        ...elementProps,
        ...{
            component: {
                component: 'ColorPicker',
                name: 'color',
                label: 'Color',
            } as RawFormComponentType,
        },
    });

    return (
        <FormElementWrapper elementProps={colorPickerProps}>
            <ReactHookFormController elementProps={colorPickerProps} />
        </FormElementWrapper>
    );
}

function FormAxis({ elementProps }: { elementProps: FormDataAPIType }) {
    const data = prepareFormComponentValues({
        Left: 'Left',
        Right: 'Right',
    });

    const axisProps = prepareFormElementProps({
        ...elementProps,
        ...{
            component: {
                component: 'FormRadioGroup',
                name: 'axis',
                label: 'Axis',
                props: {
                    row: true,
                },
            } as RawFormComponentType,
        },
    });

    return (
        <FormElementWrapper elementProps={axisProps}>
            <ReactHookFormController componentValues={data} elementProps={axisProps} />
        </FormElementWrapper>
    );
}

function FormChartType({ elementProps }: { elementProps: FormDataAPIType }) {
    const data = prepareFormComponentValues({
        Line: 'Line',
        Bar: 'Bar',
    });

    const chartTypeProps = prepareFormElementProps({
        ...elementProps,
        ...{
            component: {
                component: 'FormRadioGroup',
                name: 'chart_type',
                label: 'Type',
                props: {
                    row: true,
                },
            } as RawFormComponentType,
        },
    });

    return (
        <FormElementWrapper elementProps={chartTypeProps}>
            <ReactHookFormController componentValues={data} elementProps={chartTypeProps} />
        </FormElementWrapper>
    );
}

function FormDashSelect({ elementProps }: { elementProps: FormDataAPIType }) {
    const selectData = prepareFormComponentValues(ChartLineTypes);

    const selectProps = prepareFormElementProps({
        ...elementProps,
        ...{
            component: {
                component: 'FormSelect',
                name: 'dash_style',
                label: 'Line type',
            } as RawFormComponentType,
        },
    });

    const actualChartType = elementProps.form.hookFormGetValues('chart_type');

    return (
        <span className={actualChartType == 'Bar' ? 'd-none' : ''}>
            <FormElementWrapper elementProps={selectProps}>
                <ReactHookFormController componentValues={selectData} elementProps={selectProps} />
            </FormElementWrapper>
        </span>
    );
}

type OverlayRowData = Pick<IKpiChartOverlay, 'id'> & Partial<Omit<IKpiChartOverlay, 'id'>>;

// Mounted only while the popup is open, so it always starts from the row's current values.
// Keeping it as a separate component (rather than a hook call in the parent) means its
// react-hook-form state — including any unsaved edits — is thrown away on unmount, i.e. on
// both Cancel and Save. Without this split, Cancel left the stale edited values in place for
// the next time the popup was opened, since the form was never reset.
function EditOverlayForm({
    rowData,
    onSave,
    onCancel,
}: {
    rowData: OverlayRowData;
    onSave: (changes: Partial<OverlayRowData>) => void;
    onCancel: () => void;
}) {
    const { t } = useBundleTranslation(['components/report-content']);
    const elementProps = useCustomSimplifiedForm({
        color: rowData?.color ?? '',
        axis: rowData?.axis ?? 'Left',
        chart_type: rowData?.chart_type ?? 'Line',
        dash_style: rowData?.dash_style ?? 'Line',
    });

    elementProps.form.hookFormWatch(['chart_type']);

    const popupSettings: PopupSettings = {
        title: t('kpi_chart.edit_overlay'),
    };

    const handleSaveForm = () => {
        const values = elementProps.form.hookFormGetValues();
        onSave({
            color: values.color,
            axis: values.axis,
            chart_type: values.chart_type,
            dash_style: values.dash_style,
        });
    };

    return (
        <Popup settings={popupSettings} open onHide={onCancel} onConfirm={handleSaveForm}>
            <Stack spacing={2}>
                <FormColorPicker elementProps={elementProps} />
                <FormChartType elementProps={elementProps} />
                <FormDashSelect elementProps={elementProps} />
                <FormAxis elementProps={elementProps} />
            </Stack>
        </Popup>
    );
}

export default function KpiChartOverlayGridDataCell({ data }: CustomCellProps) {
    const { t } = useBundleTranslation(['components/report-content']);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const context = useContext(KpiChartOverlaysContext);
    const rowData = data as unknown as OverlayRowData;

    const onDeleteConfirmed = () => {
        setOpenConfirmation(false);
        context?.onDelete(rowData.id);
    };
    const { ConfirmationPopup, setOpenConfirmation } = useGridDeleteConfirmation(
        t,
        {},
        onDeleteConfirmed,
        true,
        'overlay',
    );

    return (
        <Stack direction={'row'} sx={{ height: 1, alignItems: 'center', justifyContent: 'end' }}>
            {isEdit && (
                <EditOverlayForm
                    rowData={rowData}
                    onCancel={() => setIsEdit(false)}
                    onSave={(changes) => {
                        context?.onUpdate(rowData.id, changes);
                        setIsEdit(false);
                    }}
                />
            )}
            <IconButton aria-label="edit" size={'small'} onClick={() => setIsEdit(true)}>
                <IconMi icon="gear" fontSize={'16'} />
            </IconButton>
            <IconButton aria-label="delete" size={'small'} onClick={() => setOpenConfirmation(true)}>
                <IconMi icon="delete" fontSize={'16'} />
            </IconButton>
            <ConfirmationPopup />
        </Stack>
    );
}
